<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';
// SiteUp: popup de confirmação de agendamento WAHA — montado no shell do
// dashboard pra disparar via evento global de qualquer tela sob ele (ex.: o
// painel de Kanban da conversa). A tela do Kanban (KanbanView) tem o seu próprio.
import KanbanAppointmentModal from 'dashboard/routes/dashboard/kanban/components/KanbanAppointmentModal.vue';
import { useKanbanAppointmentPopup } from 'dashboard/composables/useKanbanAppointmentPopup';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import MobileBottomNav from 'dashboard/components-next/sidebar/MobileBottomNav.vue';
import FocusModeOverlay from 'dashboard/components-next/sidebar/FocusModeOverlay.vue';
import { isConversationRouteName } from 'dashboard/helper/conversationRoutes';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
    MobileBottomNav,
    KanbanAppointmentModal,
    FocusModeOverlay,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { focusModeSetting } = useFocusMode();
    const { isListHidden } = useWorkbenchLayout();
    const { width: windowWidth } = useWindowSize();
    // SiteUp — Modo Foco: quem nunca escolheu entra focado no celular
    // (padrão mobile) e com a navegação completa no desktop. Escolha explícita
    // do usuário (true/false salvo em ui_settings) vence em qualquer tela.
    const isFocusModeActive = computed(() => {
      if (typeof focusModeSetting.value === 'boolean') {
        return focusModeSetting.value;
      }
      return windowWidth.value < 768;
    });
    const callsStore = useCallsStore();
    // SiteUp: estado do popup de agendamento (via evento global da API do Kanban).
    const {
      open: appointmentOpen,
      itemId: appointmentItemId,
      scheduledAt: appointmentScheduledAt,
    } = useKanbanAppointmentPopup();

    return {
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      isFocusModeActive,
      isListHidden,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
      appointmentOpen,
      appointmentItemId,
      appointmentScheduledAt,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
      hasRedirectedToHub: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    // SiteUp: dentro de uma conversa a bottom nav mobile some, então não
    // reservamos o padding inferior (evita espaço morto sob o campo de resposta).
    isConversationRoute() {
      return isConversationRouteName(this.$route.name);
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        this.updateUISettings({
          conversation_display_type: this.isSmallScreen
            ? LAYOUT_TYPES.EXPANDED
            : LAYOUT_TYPES.CONDENSED,
        });
      },
      immediate: true,
    },
    // SiteUp — Modo Foco: com o modo ativo, (1) a skin lights-out entra no
    // <body> (tokens do design system estilo X) e (2) a entrada padrão vira o
    // hub — só no pouso inicial em 'home', nunca em navegação já em curso.
    isFocusModeActive: {
      handler(active) {
        document.body.classList.toggle('focus-lights-out', active);
        this.maybeRedirectToHub();
      },
      immediate: true,
    },
  },
  mounted() {
    // O watcher pode disparar antes do router resolver a rota inicial —
    // revalida o pouso quando o router estiver pronto.
    this.$router.isReady().then(() => this.maybeRedirectToHub());
  },
  unmounted() {
    document.body.classList.remove('focus-lights-out');
  },
  methods: {
    maybeRedirectToHub() {
      if (
        this.isFocusModeActive &&
        !this.hasRedirectedToHub &&
        this.$route.name === 'home'
      ) {
        this.hasRedirectedToHub = true;
        this.$router.replace({
          name: 'focus_hub',
          params: { accountId: this.accountId },
        });
      }
    },
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <div
      class="focus-rail"
      :class="{ 'focus-rail--closed': isFocusModeActive }"
    >
      <div class="focus-rail__inner">
        <NextSidebar
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle-account-modal="toggleAccountModal"
          @open-key-shortcut-modal="toggleKeyShortcutModal"
          @close-key-shortcut-modal="closeKeyShortcutModal"
          @show-create-account-modal="openCreateAccountModal"
          @close-mobile-sidebar="closeMobileSidebar"
        />
      </div>
    </div>

    <!-- SiteUp V2: dock pessoal de atalhos (mockup 08). Fica FORA do <main>
         e é position:fixed — vale no sistema todo e dentro do Modo Foco,
         como o Lucas decidiu em 2026-08-10.
         DESKTOP APENAS: no celular/PWA a barra inferior já é a navegação, e o
         dock flutuava por cima da lista de conversas (arrastar num dock
         `position:fixed` também briga com o scroll por toque). -->
    <FocusModeOverlay
      v-if="isFocusModeActive && !isListHidden && !isSmallScreen"
    />

    <main
      class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
      :class="{ 'max-md:pb-14': !isConversationRoute && !isFocusModeActive }"
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <router-view />
        <CommandBar />
        <MobileBottomNav
          v-if="!isFocusModeActive"
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle-menu="toggleMobileSidebar"
        />
        <CopilotContainer />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
    <!-- SiteUp: popup de confirmação de agendamento WAHA — dispara pelo evento
         global 'kanban:appointment:scheduled' (ex.: agendar pelo painel Kanban da
         conversa). A tela do Kanban tem o seu próprio (rotas exclusivas). -->
    <KanbanAppointmentModal
      v-model="appointmentOpen"
      :item-id="appointmentItemId"
      :scheduled-at="appointmentScheduledAt"
    />
  </div>
</template>

<style scoped>
/* SiteUp — Modo Foco: colapso da barra por grid-template-columns (anima
   qualquer largura, inclusive a redimensionada pelo usuário). Curva e duração
   vêm dos tokens de movimento; reduced-motion zera pela própria variável. */
.focus-rail {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  transition: grid-template-columns var(--motion-duration-layout)
    var(--motion-ease-spring);
}
.focus-rail--closed {
  grid-template-columns: minmax(0, 0fr);
}
@media (min-width: 768px) {
  .focus-rail--closed {
    pointer-events: none;
  }
}
.focus-rail__inner {
  display: flex;
  min-width: 0;
  overflow: hidden;
}
</style>
